import { NewContractorAttribute } from "@/models/api/administration/contractors/NewContractorAttribute";
import {
  ContractorAttribute,
  ContractorAttributeResponse,
} from "@/models/api/administration/contractors/ContractorAttribute";
import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { AttributeInfo } from "@/models/api/administration/contractors/AttributeInfo";

//#region routes
export const URL_GET_GLOBAL_CONTRACTOR_ATTRIBUTES = "/ContractorAdministration/${ContractorId}/GlobalAttributes";
export const URL_POST_ADD_GLOBAL_CONTRACTOR_ATTRIBUTES =
  "/ContractorAdministration/${ContractorId}/GlobalAttributes/Add";
export const URL_GET_CONTRACTOR_ATTRIBUTES_PAGED = "/ContractorAdministration/${ContractorId}/Attributes/Paged";
export const URL_POST_ADD_TO_ALL_SERVICE_CONTRACTOR_ATTRIBUTES =
  "/ContractorAdministration/${ContractorId}/Attributes/Add/All";
export const URL_POST_ADD_CONTRACTOR_ATTRIBUTE_TO_SERVICE_TYPE =
  "/ContractorAdministration/${ContractorId}/ServiceType/${ServiceTypeId}/Attribute/Add";
export const URL_POST_ADD_CONTRACTOR_ATTRIBUTES_WHERE_EMPTY =
  "/ContractorAdministration/${ContractorId}/Attributes/Add/Empty";
export const URL_POST_EDIT_FILLED_CONTRACTOR_ATTRIBUTES =
  "/ContractorAdministration/${ContractorId}/Attributes/Edit/Filled";
export const URL_POST_REMOVE_CONTRACTOR_ATTRIBUTES =
  "/ContractorAdministration/${ContractorId}/Attributes/${AttributePrototypeId}/Remove";
export const URL_POST_ADD_CONTRACTOR_ATTRIBUTES_FILTER =
  "/ContractorAdministration/${ContractorId}/Attributes/Add/Filter";
export const URL_POST_SWAP_ATTRIBUTE_ORDER = "/ContractorAdministration/${ContractorId}/Attributes/Swap";
export const URL_POST_REMOVE_GLOBAL_CONTRACTOR_ATTRIBUTE =
  "/Attribute/Contractor/Global/${GlobalContractorAttributeId}/Remove";
export const URL_POST_EDIT_GLOBAL_CONTRACTOR_ATTRIBUTE =
  "/Attribute/Contractor/Global/${GlobalContractorAttributeId}/Edit";
export const URL_POST_EDIT_CONTRACTOR_ATTRIBUTE_VALUE = "/Attribute/Contractor/${ContractorAttributeId}/Edit/Value";
export const URL_POST_REMOVE_CONTRACTOR_ATTRIBUTE = "/Attribute/Contractor/${ContractorAttributeId}/Remove";
export const URL_GET_SERVICE_TYPE_ATTRIBUTES = "/ContractorAdministration/${ServiceTypeId}/ServiceTypeAttributes";
//#endregion

const _getServiceTypeAttributes = new CancellableHelper<Array<AttributeInfo>>(
  URL_GET_SERVICE_TYPE_ATTRIBUTES,
  AttributeInfo
);
export const getServiceTypeAttributes = async (serviceTypeId: number): Promise<Array<AttributeInfo>> => {
  return _getServiceTypeAttributes.get({
    ServiceTypeId: serviceTypeId,
  });
};

const _getGlobalContractorAttributes = new CancellableHelper<Array<ContractorAttribute>>(
  URL_GET_GLOBAL_CONTRACTOR_ATTRIBUTES,
  ContractorAttribute
);
export const getGlobalContractorAttributes = async (contractorId: string): Promise<Array<ContractorAttribute>> => {
  return _getGlobalContractorAttributes.get({
    ContractorId: contractorId,
  });
};

const _addGlobalContractorAttributes = new CancellableHelper<string>(URL_POST_ADD_GLOBAL_CONTRACTOR_ATTRIBUTES);
export const addGlobalContractorAttributes = async (
  contractorId: string,
  contractorAttribute: NewContractorAttribute
): Promise<string> => {
  return _addGlobalContractorAttributes.post(
    {
      ContractorId: contractorId,
    },
    contractorAttribute
  );
};

const _addContractorAttributeToServiceType = new CancellableHelper<null>(
  URL_POST_ADD_CONTRACTOR_ATTRIBUTE_TO_SERVICE_TYPE,
  null,
  true
);
export const addContractorAttributeToServiceType = async (
  contractorId: string,
  serviceTypeId: number,
  contractorAttribute: NewContractorAttribute
): Promise<null> => {
  return _addContractorAttributeToServiceType.post(
    {
      ContractorId: contractorId,
      ServiceTypeId: serviceTypeId,
    },
    contractorAttribute
  );
};

const _removeGlobalContractorAttribute = new CancellableHelper<null>(
  URL_POST_REMOVE_GLOBAL_CONTRACTOR_ATTRIBUTE,
  null,
  true
);
export const removeGlobalContractorAttribute = async (globalContractorAttributeId: string): Promise<null> => {
  return _removeGlobalContractorAttribute.post({
    GlobalContractorAttributeId: globalContractorAttributeId,
  });
};

const _editGlobalContractorAttribute = new CancellableHelper<null>(
  URL_POST_EDIT_GLOBAL_CONTRACTOR_ATTRIBUTE,
  null,
  true
);
export const editGlobalContractorAttribute = async (
  globalContractorAttributeId: string,
  contractorAttribute: NewContractorAttribute
): Promise<null> => {
  return _editGlobalContractorAttribute.post(
    {
      GlobalContractorAttributeId: globalContractorAttributeId,
    },
    contractorAttribute
  );
};

const _editContractorAttributeValue = new CancellableHelper<null>(URL_POST_EDIT_CONTRACTOR_ATTRIBUTE_VALUE, null, true);
export const editContractorAttributeValue = async (contractorAttributeId: string, newValue: string): Promise<null> => {
  return _editContractorAttributeValue.post(
    {
      ContractorAttributeId: contractorAttributeId,
    },
    newValue
  );
};

const _getContractorAttributesPaged = new CancellableHelper<ContractorAttributeResponse>(
  URL_GET_CONTRACTOR_ATTRIBUTES_PAGED,
  ContractorAttributeResponse
);
_getContractorAttributesPaged.jsonSerializer = true;
export const getContractorAttributesPaged = async (
  genericTableRequest: GenericTableRequest,
  contractorId: string
): Promise<ContractorAttributeResponse> => {
  return _getContractorAttributesPaged.get({
    ContractorId: contractorId,
    ...genericTableRequest,
  });
};

const _postSwapAttributeOrder = new CancellableHelper<void>(URL_POST_SWAP_ATTRIBUTE_ORDER, null, true);
export const postSwapAttributeOrder = async (
  contractorId: string,
  attributeId: string,
  swapUp: boolean,
  serviceTypeId: number | null
): Promise<void> => {
  return _postSwapAttributeOrder.post(
    {
      ContractorId: contractorId,
      AttributeId: attributeId,
      SwapUp: swapUp,
      ServiceTypeId: serviceTypeId,
    },
    null
  );
};

const _addToAllServiceContractorAttributes = new CancellableHelper<null>(
  URL_POST_ADD_TO_ALL_SERVICE_CONTRACTOR_ATTRIBUTES,
  null,
  true
);
export const addToAllServiceContractorAttributes = async (
  contractorId: string,
  body: NewContractorAttribute
): Promise<null> => {
  return _addToAllServiceContractorAttributes.post(
    {
      ContractorId: contractorId,
    },
    body
  );
};

const _removeContractorAttribute = new CancellableHelper<null>(URL_POST_REMOVE_CONTRACTOR_ATTRIBUTE, null, true);
export const removeContractorAttribute = async (contractorAttributeId: string): Promise<null> => {
  return _removeContractorAttribute.post({
    ContractorAttributeId: contractorAttributeId,
  });
};

const _editFilledContractorAttributes = new CancellableHelper<null>(
  URL_POST_EDIT_FILLED_CONTRACTOR_ATTRIBUTES,
  null,
  true
);
export const editFilledContractorAttributes = async (
  contractorId: string,
  body: NewContractorAttribute
): Promise<null> => {
  return _editFilledContractorAttributes.post(
    {
      ContractorId: contractorId,
    },
    body
  );
};

const _removeContractorAttributes = new CancellableHelper<null>(URL_POST_REMOVE_CONTRACTOR_ATTRIBUTES, null, true);
export const removeContractorAttributes = async (contractorId: string, attributePrototypeId: number): Promise<null> => {
  return _removeContractorAttributes.post({
    ContractorId: contractorId,
    AttributePrototypeId: attributePrototypeId,
  });
};

const _addContractorAttributesWhereEmpty = new CancellableHelper<null>(
  URL_POST_ADD_CONTRACTOR_ATTRIBUTES_WHERE_EMPTY,
  null,
  true
);
export const addContractorAttributesWhereEmpty = async (
  contractorId: string,
  body: NewContractorAttribute
): Promise<null> => {
  return _addContractorAttributesWhereEmpty.post(
    {
      ContractorId: contractorId,
    },
    body
  );
};

const _addContractorAttributesFiltered = new CancellableHelper<null>(
  URL_POST_ADD_CONTRACTOR_ATTRIBUTES_FILTER,
  null,
  true
);
_addContractorAttributesFiltered.jsonSerializer = true;
export const addContractorAttributesFiltered = async (
  contractorId: string,
  body: NewContractorAttribute,
  genericTableRequest: GenericTableRequest
): Promise<null> => {
  return _addContractorAttributesFiltered.post(
    {
      ContractorId: contractorId,
      ...genericTableRequest,
    },
    body
  );
};
